import { cutoutKey, useFilterStore } from '@/stores/Configurator/FilterStore';
import { useDebounceFn } from '@vueuse/core';
import { onBeforeMount } from 'vue';
import StateUpdateRequest from '@/communication/StateUpdateRequest';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore';
import { useSizeStore } from '@/stores/Configurator/SizeStore';
import { useExtraOptionsStore } from '@/stores/ExtraOptionsStore';
import { useUrl } from './UseUrl';
import { ConfiguratorStep } from '@/types/Configurator/ConfiguratorStep';
import { MediaclipProductType } from '@/types/Mediaclip/MediaclipProductType';
import { useMediaclipStore } from '@/stores/Configurator/MediaclipStore';

const urlComposable = useUrl();
const continueTokenKey = 'continue-token';
const returnKey = 'return';

export function useContinueToken() {
  const stateUpdateRequest = new StateUpdateRequest();

  const configuratorStore = useConfiguratorStore();
  const filterStore = useFilterStore();
  const sizeStore = useSizeStore();
  const extraOptionStore = useExtraOptionsStore();
  const mediaclipStore = useMediaclipStore();

  function loadDataFromState(): void {
    // Initialize configurator state data
    if (window.state_data?.configurator) {
      configuratorStore.$patch(window.state_data.configurator);
    }

    // Initialize filter state data
    if (window.state_data?.filter) {
      filterStore.$patch(window.state_data.filter);
    }

    // Initialize size state data
    if (window.state_data?.size) {
      sizeStore.$patch(window.state_data.size);
    }

    if (window.state_data?.quote_item_id) {
      configuratorStore.quoteItemId = window.state_data?.quote_item_id;
    }

    if (window.state_data?.extra_options) {
      extraOptionStore.$patch(window.state_data.extra_options);
    }

    if (window.state_data?.mediaclip) {
      mediaclipStore.$patch(window.state_data.mediaclip);
    }

    // Get "return" value from url
    const returnValue = urlComposable.getParameterFromUrl(returnKey);
    if (returnValue) {
      // Set configurator as current step and remove parameter from url
      configuratorStore.currentStep =
        configuratorStore.product.mediaclip_product_type ===
        MediaclipProductType.SIMPLE
          ? ConfiguratorStep.HalfConfigurator
          : ConfiguratorStep.Configurator;

      // Set price on product page to the selected sku price
      window.localStorage.setItem(
        `product-${window.product.entity_id}-price`,
        String(sizeStore.selectedSizeValue.attributes.price / 100)
      );

      urlComposable.removeParameterFromUrl(returnKey);
      configuratorStore.isEditing = true;

      // Handle the most chose badge if we're on mobile
      if (window.matchMedia('(max-width: 767px)').matches) {
        sizeStore.handleMostChosenBadgeVisibility(false);
      }

      // Set default filter sku when on upload configurator (needed to set the label on the right filter value)
      if (configuratorStore.isUploadConfigurator) {
        filterStore.setDefaultFilterSku(
          cutoutKey,
          useSizeStore().imageDimensions,
          false
        );
      }
    }

    // Remove continue token from url if data is loaded
    const continueToken = urlComposable.getParameterFromUrl(continueTokenKey);
    if (continueToken) {
      configuratorStore.identifier = continueToken;
      urlComposable.removeParameterFromUrl(continueTokenKey);
    }
  }

  async function saveStateInDatabase() {
    if (!configuratorStore.identifier) {
      return;
    }

    const sizeStore = useSizeStore();
    const filterStore = useFilterStore();
    const extraOptionStore = useExtraOptionsStore();
    const mediaclipStore = useMediaclipStore();

    await stateUpdateRequest
      .setIdentifier(configuratorStore.identifier)
      .setData({
        configurator: {
          currentStep: configuratorStore.currentStep,
          mediaclipToken: configuratorStore.mediaclipToken,
          mediaclipProjectId: configuratorStore.mediaclipProjectId,
          mediaclipPreviewUrl: configuratorStore.mediaclipPreviewUrl,
          identifier: configuratorStore.identifier,
        },
        filter: {
          selectedFilterOptions: filterStore.selectedFilterOptions,
        },
        size: {
          selectedSizeOption: sizeStore.selectedSizeOption,
          imageDimensions: sizeStore.imageDimensions,
        },
        extra_options: {
          selectedOptions: extraOptionStore.selectedOptions,
        },
        mediaclip: {
          filledWorksheets: mediaclipStore.filledWorksheets,
          emptyWorksheets: mediaclipStore.emptyWorksheets,
        },
      })
      .getResponse();

    urlComposable.removeParameterFromUrl(continueTokenKey);
  }

  const saveStates = useDebounceFn(() => saveStateInDatabase(), 4000, {
    maxWait: 30000,
  });

  function watchForChanges() {
    configuratorStore.$subscribe(() => {
      saveStates();
    });

    filterStore.$subscribe(() => {
      saveStates();
    });

    extraOptionStore.$subscribe(() => {
      saveStates();
    });

    mediaclipStore.$subscribe(() => {
      saveStates();
    });

    window.addEventListener('beforeunload', () => {
      saveStateInDatabase();
    });
  }

  onBeforeMount(() => {
    loadDataFromState();
  });

  return {
    watchForChanges,
    saveStateInDatabase,
  };
}
